/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2021, 2024
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import _ from 'lodash';
import * as yup from 'yup';
import { TestContext } from 'yup';

import { SERVER_URL_REGEX } from '../../../ia/util/regex';
import { MAX_PASSWORD_LENGTH } from '../../util/constants';
import { NO_ONLY_SPACES_REGEX } from '../../util/validation/constants';
import { ConnectStepMessages } from '../segment/connect-step-tab/messages';
import { AuthenticationStepMessages } from './authentication-tab/messages';
import {
  AUTHENTICATION_METHODS,
  BEARER,
  BODY,
  CONTENT_APPLICATION_FORM_URLENCODED,
  OAUTH2_AUTHORIZATION_FLOW_REDIRECT_URI,
  VALUE
} from './constants';

export const FIELDS = {
  AUTHENTICATION: 'authentication',
  AUTHENTICATION_METHOD: 'authentication.authentication_method',
  AUTHENTICATION_DISPLAY_NAME: 'authentication.display_name',
  CATALOG_ITEM_ID: 'catalog_item_id',
  GENERATED_SERVER_URL: 'generated_server_url',
  PRIVATE_AUTHENTICATION: 'private.secrets.authentication',
  SERVER_URL: 'server_url',
  SERVER_VARIABLES: 'server_variables',
  SERVER_VARIABLE_TYPE: (variable: string) =>
    `server_variables.${variable}.type`,
  SERVER_VARIABLE_VALUE: (variable: string) =>
    `server_variables.${variable}.value`,
  BASIC: {
    USERNAME: 'authentication.basic.username.value',
    PASSWORD: 'private.secrets.authentication.basic.password.value',
    PASSWORD_TYPE: 'private.secrets.authentication.basic.password.type'
  },
  BEARER: {
    TOKEN: 'private.secrets.authentication.bearer.token.value',
    TOKEN_TYPE: 'private.secrets.authentication.bearer.token.type'
  },
  APIKEY: {
    AUTH: 'private.secrets.authentication.api_key',
    VALUE: (key: string) =>
      `private.secrets.authentication.api_key.${key}.value`,
    TYPE: (key: string) => `private.secrets.authentication.api_key.${key}.type`,
    IN: (key: string) => `private.secrets.authentication.api_key.${key}.in`
  },
  OAUTH2: {
    FLOWS: 'authentication.oauth2.flows',
    PREFERRED_FLOW: 'authentication.oauth2.preferred_flow',
    USERNAME: (flow: string) =>
      `authentication.oauth2.flows.${flow}.username.value`,
    PRIVATE_SECRETS: 'private.secrets',
    FLOWS_FLOW: (flow: string) =>
      `private.secrets.authentication.oauth2.flows.${flow}`,
    PASSWORD: (flow: string) =>
      `private.secrets.authentication.oauth2.flows.${flow}.password.value`,
    CLIENT_ID: (flow: string) =>
      `private.secrets.authentication.oauth2.flows.${flow}.client_id.value`,
    CLIENT_SECRET: (flow: string) =>
      `private.secrets.authentication.oauth2.flows.${flow}.client_secret.value`,
    AUTHORIZATION_CODE:
      'private.secrets.authentication.oauth2.flows.authorization_code.authorization_code',
    CLIENT_AUTH_TYPE: (flow: string) =>
      `authentication.oauth2.flows.${flow}.client_auth_type`,
    AUTHORIZATION_URL:
      'authentication.oauth2.flows.authorization_code.authorization_url',
    TOKEN_URL: (flow: string) =>
      `authentication.oauth2.flows.${flow}.token_url`,
    REFRESH_URL: (flow: string) =>
      `authentication.oauth2.flows.${flow}.refresh_url`,
    SCOPES: (flow: string) => `authentication.oauth2.flows.${flow}.scopes`,
    HEADER_PREFIX: (flow: string) =>
      `authentication.oauth2.flows.${flow}.header_prefix`,
    SECRET_TYPE: (flow: string, field: string) =>
      `private.secrets.authentication.oauth2.flows.${flow}.${field}.type`,
    CUSTOM: {
      FLOW: (flow: string) => `authentication.oauth2.flows.custom.${flow}`,
      GRANT_TYPE: (flow: string) =>
        `authentication.oauth2.flows.custom.${flow}.grant_type`,
      REFRESH_URL: (flow: string) =>
        `authentication.oauth2.flows.custom.${flow}.refresh_url`,
      TOKEN_URL: (flow: string) =>
        `authentication.oauth2.flows.custom.${flow}.token_url`,
      PRIVATE_FLOW: (flow: string) =>
        `private.secrets.authentication.oauth2.flows.custom.${flow}`,
      SECRET_TYPE: (flow: string, secretName: string) =>
        `private.secrets.authentication.oauth2.flows.custom.${flow}.custom_secrets.${secretName}.type`,
      SECRET_VALUE: (flow: string, secretName: string) =>
        `private.secrets.authentication.oauth2.flows.custom.${flow}.custom_secrets.${secretName}.value`,
      PARAM_TYPE: (flow: string, paramName: string) =>
        `authentication.oauth2.flows.custom.${flow}.params.${paramName}.type`,
      PARAM_VALUE: (flow: string, paramName: string) =>
        `authentication.oauth2.flows.custom.${flow}.params.${paramName}.value`,
      AUTH_TYPE: (flow: string) =>
        `authentication.oauth2.flows.custom.${flow}.client_auth_type`,
      HEADER_PREFIX: (flow: string) =>
        `authentication.oauth2.flows.custom.${flow}.header_prefix`,
      SCOPES: (flow: string) =>
        `authentication.oauth2.flows.custom.${flow}.scopes`
    }
  }
};

interface IntegrationKeyValuePairProps {
  defaultType?: string;
  defaultValue?: string;
  test?: {
    name: string;
    error: string | Record<string, string>;
    testContext: (value: string, context: TestContext) => boolean;
  };
}
const IntegrationKeyValuePair = ({
  defaultType,
  defaultValue,
  test
}: IntegrationKeyValuePairProps = {}) => {
  let keyType = yup.string();
  if (!_.isNil(defaultType)) {
    keyType = keyType.default(defaultType);
  } else {
    keyType = keyType.default(VALUE);
  }

  let value = yup.string().nullable();
  if (!_.isNil(defaultValue)) {
    value = value.default(defaultValue);
  }
  if (!_.isNil(test)) {
    const { name, error, testContext } = test;
    value = value.test(name, error, testContext);
  }

  return yup.object().shape({
    type: keyType,
    value
  });
};

const OAuthPair = (props: IntegrationKeyValuePairProps = {}) =>
  IntegrationKeyValuePair(props).concat(
    yup.object().shape({
      expires_by: yup.string(),
      lifespan: yup.string()
    })
  );

const Flow = yup.object().shape({
  token_url: yup.string(),
  refresh_url: yup.string(),
  client_auth_type: yup.string().default(BODY),
  content_type: yup.string().default(CONTENT_APPLICATION_FORM_URLENCODED),
  header_prefix: yup.string().default(BEARER),
  scopes: yup.object()
});

const PasswordFlow = Flow.concat(
  yup.object().shape({
    username: IntegrationKeyValuePair({
      test: {
        name: 'OAuth2UsernameValidation',
        error: AuthenticationStepMessages.usernameRequired,
        testContext: (_value, _context: TestContext) => true
      }
    })
  })
);

const AuthorizationFlow = Flow.concat(
  yup.object().shape({
    authorization_url: yup.string(),
    redirect_uri: yup
      .string()
      .default(OAUTH2_AUTHORIZATION_FLOW_REDIRECT_URI()),
    // `is_from_tooling` is needed by backend to differntiate whether
    // the PUT call to /definitions endpoint is made from tooling or
    // from Webhooks.
    is_from_tooling: yup.boolean().default(true)
  })
);

const SecretsFlow = yup.object().shape({
  client_id: IntegrationKeyValuePair(),
  client_secret: IntegrationKeyValuePair(),
  access_token: OAuthPair(),
  refresh_token: OAuthPair()
});

const PasswordSecretsFlow = SecretsFlow.concat(
  yup.object().shape({
    password: IntegrationKeyValuePair({
      test: {
        name: 'OAuth2PasswordValidation',
        error: AuthenticationStepMessages.passwordRequired,
        testContext: (_value, _context: TestContext) => true
      }
    })
  })
);

const AuthorizationCodeSecretsFlow = SecretsFlow.concat(
  yup.object().shape({
    authorization_code: yup.string()
  })
);

const ServerVariablesSchema = yup.lazy(obj =>
  yup.object(
    _.mapValues(obj, () =>
      yup.object().shape({
        type: yup.string().default('value'),
        default: yup.string().default(''),
        description: yup.string().default(''),
        value: yup.string().matches(NO_ONLY_SPACES_REGEX, {
          message: AuthenticationStepMessages.serverVariablesNoEmpty,
          excludeEmptyString: true
        })
      })
    )
  )
);

export const BaseExtensionsSettingsSchema = (isSegment: boolean) =>
  yup.object().shape({
    server_url: yup.string(),
    catalog_item_id: yup.string(),
    authentication: yup.object().shape({
      authentication_method: yup.string().default(AUTHENTICATION_METHODS.NONE),
      display_name: yup.string().nullable().default(null),
      basic: yup.object().shape({
        username: IntegrationKeyValuePair({
          test: {
            name: 'BasicAuthUsernameValidation',
            error: isSegment
              ? ConnectStepMessages.required
              : AuthenticationStepMessages.usernameRequired,
            testContext: (value: string, context: TestContext) => {
              const { from } = context as any;
              const authMethod = _.get(
                from?.pop()?.value,
                FIELDS.AUTHENTICATION_METHOD
              );
              return (
                !_.isEqual(authMethod, AUTHENTICATION_METHODS.BASIC) ||
                !_.isEmpty(value?.trim())
              );
            }
          }
        })
      }),
      oauth2: yup.object().shape({
        preferred_flow: yup.string(),
        flows: yup.object().shape({
          password: PasswordFlow,
          client_credentials: Flow,
          authorization_code: AuthorizationFlow
        })
      })
    }),
    private: yup
      .object()
      .shape({
        secrets: yup
          .object()
          .shape({
            authentication: yup.object().shape({
              basic: yup.object().shape({
                password: IntegrationKeyValuePair({
                  test: {
                    name: 'BasicAuthPasswordValidation',
                    error: AuthenticationStepMessages.passwordMaxChars,
                    testContext: (value: string, context: TestContext) => {
                      const { from } = context as any;
                      const authMethod = _.get(
                        from?.pop()?.value,
                        FIELDS.AUTHENTICATION_METHOD
                      );

                      return (
                        !_.isEqual(authMethod, AUTHENTICATION_METHODS.BASIC) ||
                        value.length <= MAX_PASSWORD_LENGTH
                      );
                    }
                  }
                })
              }),
              bearer: yup.object().shape({
                token: IntegrationKeyValuePair({
                  test: {
                    name: 'BearerAuthTokenValidation',
                    error: AuthenticationStepMessages.tokenRequired,
                    testContext: (value: string, context: TestContext) => {
                      const { from } = context as any;
                      const authMethod = _.get(
                        from?.pop()?.value,
                        FIELDS.AUTHENTICATION_METHOD
                      );
                      return (
                        !_.isEqual(authMethod, AUTHENTICATION_METHODS.BEARER) ||
                        !_.isEmpty(value?.trim())
                      );
                    }
                  }
                })
              }),
              api_key: yup.lazy(obj =>
                yup.object(
                  _.mapValues(obj, () =>
                    IntegrationKeyValuePair({
                      test: {
                        name: 'ApiKeyValidation',
                        error: AuthenticationStepMessages.apiKeyRequired,
                        testContext: (value: string, context: TestContext) => {
                          const { from } = context as any;
                          const authMethod = _.get(
                            from?.pop()?.value,
                            FIELDS.AUTHENTICATION_METHOD
                          );
                          return (
                            !_.isEqual(
                              authMethod,
                              AUTHENTICATION_METHODS.API_KEY
                            ) || !_.isEmpty(value?.trim())
                          );
                        }
                      }
                    })
                  )
                )
              ),
              oauth2: yup.object().shape({
                flows: yup.object().shape({
                  password: PasswordSecretsFlow,
                  client_credentials: SecretsFlow,
                  authorization_code: AuthorizationCodeSecretsFlow
                })
              })
            })
          })
          .nullable()
          .default(undefined)
      })
      .nullable()
      .default(undefined)
  });

export const settingsSchema = BaseExtensionsSettingsSchema(false).concat(
  yup.object().shape({
    server_variables: ServerVariablesSchema,
    generated_server_url: yup
      .string()
      .required(AuthenticationStepMessages.serverUrlRequired)
      .matches(SERVER_URL_REGEX, {
        message: AuthenticationStepMessages.serverUrlInvalid
      })
  })
);
